/**
 * WeekNav — Wochennavigation (vorherige/nächste Woche) mit KW-Label.
 *
 * Mitte: Kalenderwoche + Jahr (fett) und darunter der Mo–Fr-Datumsbereich
 * (z. B. „19.10. bis 23.10."). Die Vor/Zurück-Buttons zeigen zusätzlich die KW
 * und den Datumsbereich der jeweiligen Zielwoche. Das Navigationsverhalten
 * (`goToWeek(±1)`) bleibt unverändert; der Wochenbezeichner ist weiterhin der
 * ISO-Wochen-String (`isoWeek`).
 */

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useI18n } from "../../i18n/I18nProvider";
import { addWeeks } from "../../lib/isoWeek";
import { isoWeekDateRange, isoWeekNumber } from "./helpers";

export default function WeekNav({
  isoWeek,
  onPrev,
  onNext,
}: {
  isoWeek: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t, formatDate } = useI18n();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("sm"));

  // Mobil kompakter Trenner („–"), ab sm der ausgeschriebene („ bis "/" to ").
  const sep = wide ? t("weeklyPlan.nav.rangeSep") : t("weeklyPlan.nav.rangeSepShort");
  const year = isoWeek.slice(0, 4);

  const prevWeek = addWeeks(isoWeek, -1);
  const nextWeek = addWeeks(isoWeek, 1);

  const currentRange = isoWeekDateRange(isoWeek, formatDate, sep);
  const prevRange = isoWeekDateRange(prevWeek, formatDate, sep);
  const nextRange = isoWeekDateRange(nextWeek, formatDate, sep);

  // Schriftgrößen: mobil knapp, ab sm etwas luftiger.
  const labelSize = wide ? 13 : 11;
  const rangeSize = wide ? 12 : 10;
  const centerWeekSize = wide ? 16 : 13;
  const centerRangeSize = wide ? 14 : 11.5;
  const iconFont = wide ? 22 : 18;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={0.25}
      px={0.5}
      pb={0.25}
    >
      <Button
        color="inherit"
        onClick={onPrev}
        aria-label={t("weeklyPlan.nav.prev")}
        startIcon={<ChevronLeft sx={{ fontSize: iconFont }} />}
        sx={{
          textTransform: "none",
          minHeight: 44,
          flexShrink: 0,
          px: 0.75,
          "& .MuiButton-startIcon": { mr: 0.25 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1 }}>
          <Typography component="span" fontWeight={600} fontSize={labelSize} lineHeight={1.15}>
            {t("weeklyPlan.nav.weekLabel", { week: isoWeekNumber(prevWeek) })}
          </Typography>
          <Typography component="span" color="text.secondary" fontSize={rangeSize} lineHeight={1.05}>
            {prevRange}
          </Typography>
        </Box>
      </Button>

      <Box textAlign="center" minWidth={0}>
        <Typography fontWeight={700} fontSize={centerWeekSize} aria-live="polite" noWrap lineHeight={1.2}>
          {t("weeklyPlan.nav.weekLabel", { week: isoWeekNumber(isoWeek) })} · {year}
        </Typography>
        <Typography color="text.secondary" fontSize={centerRangeSize} noWrap lineHeight={1.2}>
          {currentRange}
        </Typography>
      </Box>

      <Button
        color="inherit"
        onClick={onNext}
        aria-label={t("weeklyPlan.nav.next")}
        endIcon={<ChevronRight sx={{ fontSize: iconFont }} />}
        sx={{
          textTransform: "none",
          minHeight: 44,
          flexShrink: 0,
          px: 0.75,
          "& .MuiButton-endIcon": { ml: 0.25 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1 }}>
          <Typography component="span" fontWeight={600} fontSize={labelSize} lineHeight={1.15}>
            {t("weeklyPlan.nav.weekLabel", { week: isoWeekNumber(nextWeek) })}
          </Typography>
          <Typography component="span" color="text.secondary" fontSize={rangeSize} lineHeight={1.05}>
            {nextRange}
          </Typography>
        </Box>
      </Button>
    </Stack>
  );
}
